import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ScanLine, Camera, CameraOff, Flashlight, FlashlightOff, Focus, Loader2 } from "lucide-react";
import { normalizeScan } from "@/lib/scan";
import { unlockAudio } from "@/lib/sound";

// Tracks the most-recently-focused scan input so the window-focus self-heal only
// restores the field the operator was actually using — never hijacks a notes box,
// dropdown, or a second scan field on the same screen.
let lastScanInput = null;

// SINGLE-CAMERA LOCK — a device typically allows only ONE live rear-camera stream at a
// time. When two ScanFields live on the same screen (e.g. destination + part on Put Away
// / Move Item / Move Tote), starting one must FULLY release any other and AWAIT that
// release before acquiring — otherwise the second stream resolves with no frames (a blank
// window). `activeReleaseRef` holds the currently-live scanner's release fn (a ref so it
// always points at the latest closure).
let activeReleaseRef = null;
async function claimCamera(releaseRef) {
  if (activeReleaseRef && activeReleaseRef !== releaseRef) {
    try { await activeReleaseRef.current?.(); } catch { /* ignore */ }
  }
  activeReleaseRef = releaseRef;
}
function unclaimCamera(releaseRef) {
  if (activeReleaseRef === releaseRef) activeReleaseRef = null;
}
const CAM_SETTLE_MS = 250;      // let the OS free the device before re-acquiring
const CAM_FRAME_TIMEOUT_MS = 1500;  // how long to wait for real frames before treating as blank

// Reusable 3-input scanner: phone camera QR/barcode + scan gun (keyboard wedge)
// + manual typing. After every scan it clears + refocuses for rapid gun capture.
export function ScanField({ onScan, label, placeholder = "Scan gun or type code…",
                            submitLabel = "Submit", testid = "scan", busy = false, autoFocus = true,
                            continuous = false, autoStartCamera = false, uppercase = false,
                            onValueChange = null, big = false, paused = false }) {
  const cameraId = `${testid}-camera-reader`;
  const [scan, setScan] = useState("");
  const [camOn, setCamOn] = useState(autoStartCamera);
  const [camErr, setCamErr] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [focusSupported, setFocusSupported] = useState(false);
  const inputRef = useRef(null);
  const qrRef = useRef(null);
  const mountedRef = useRef(true);
  // THE CAMERA'S DECODE CALLBACK IS REGISTERED WITH THE DRIVER **ONCE**, when the camera starts —
  // so anything it closes over is frozen at that moment. `onScan` is rebuilt on every render by
  // screens whose meaning CHANGES mid-run (floor put-away: "scan where it's going" -> "scan part
  // after part"), and the frozen callback kept calling the FIRST one. 2026-10-08, his crew, in a
  // lift: bin scanned fine, then every part after it was sent to the server as a LOCATION and
  // refused — "THAT'S A PART, NOT A PLACE" — while the bin sat pinned at the top of the screen
  // looking loaded. Reproduced with a fake camera: 0 of 3 parts went away, all 3 sent as `bin`.
  // A ref read at call time means the camera always runs the CURRENT handler.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const lastScanRef = useRef({ code: "", ts: 0 });   // duplicate-scan debounce
  // HOW THE CODE GOT INTO THE BOX, handed to the screen so it can be recorded ON the move.
  // A scan gun IS a keyboard: it types a whole code in a few milliseconds, which no hand can do,
  // and often commits several characters in a single event. That makes gun-vs-typed a JUDGEMENT,
  // never proof — the server stores it as one. Only the camera is certain.
  const keysRef = useRef({ first: 0, last: 0, events: 0, maxJump: 0 });
  const noteTyping = (prevLen, nextLen) => {
    const t = Date.now();
    const k = keysRef.current;
    if (!k.events || t - k.last > 1500) {
      keysRef.current = { first: t, last: t, events: 1, maxJump: Math.max(0, nextLen - prevLen) };
    } else {
      k.last = t; k.events += 1; k.maxJump = Math.max(k.maxJump, nextLen - prevLen);
    }
  };
  const judgeInput = () => {
    const k = keysRef.current;
    if (k.maxJump >= 4) return "gun";                                        // 4+ chars in one event
    if (k.events >= 4 && (k.last - k.first) / (k.events - 1) <= 25) return "gun";
    return k.events ? "typed" : "unknown";
  };
  // CONTINUOUS camera: a tag sitting in the lens decodes on EVERY frame. Lock a code
  // once accepted, then re-arm ONLY after it physically leaves the frame (counted via
  // consecutive empty frames). Kills the rapid re-read "machine-gun" with no fragile timer.
  const armedCodeRef = useRef(null);
  const armedMissRef = useRef(0);
  // A SCAN GUN IS A KEYBOARD. It types the code into this box character by character, so a
  // QR holding {site}/scan/part/<token> arrives as a typed URL — which is how a full link
  // ended up in a tag box. The URL is stripped the moment it is recognisable, and from
  // that point this box holds an opaque TOKEN: uppercasing is suspended until the next
  // submit, because a token is case-sensitive and `.toUpperCase()` would break it.
  const tokenModeRef = useRef(false);
  const decodedOnceRef = useRef(false);   // H1: one-shot guard for the non-continuous decode
  const releaseRef = useRef(null);        // H2: stable handle to this instance's camera release
  const camOnRef = useRef(camOn);
  camOnRef.current = camOn;
  // A question is on screen (a reason box, "already on another cart?"): the camera stays ON but takes nothing in
  // until it is answered — a part passing the lens can never replace the question being asked.
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  // Refocus AFTER the re-render settles (double rAF), guarded against unmount —
  // so the big "last" flash / log update / camera teardown can't swallow it.
  // NEVER while the camera scanner is live: on a tablet, focusing the manual input
  // pops the on-screen keyboard right over the camera view.
  const refocus = () => {
    if (!autoFocus || camOnRef.current || qrRef.current) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (mountedRef.current && !camOnRef.current && inputRef.current) inputRef.current.focus();
    }));
  };

  useEffect(() => {
    mountedRef.current = true;
    refocus();
    // Self-heal: if a stray click/blur drops focus to nothing, restore it — but
    // only for the field the operator last used, and only when nothing else is
    // focused (never steals from notes/dropdowns/another scan field).
    const onWinFocus = () => {
      if (!autoFocus || qrRef.current) return;          // skip while camera is running
      const ae = document.activeElement;
      if ((!ae || ae === document.body) && lastScanInput === inputRef.current) {
        inputRef.current?.focus();
      }
    };
    window.addEventListener("focus", onWinFocus);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("focus", onWinFocus);
      stopCameraQuiet();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!camOn) return;
    let active = true;
    decodedOnceRef.current = false;
    // Camera is the active input now — drop focus off the manual field so the
    // tablet on-screen keyboard doesn't pop up over the live camera view.
    try { inputRef.current?.blur?.(); } catch { /* ignore */ }
    try { document.activeElement?.blur?.(); } catch { /* ignore */ }

    const decodeCb = (decoded) => {
      if (!active) return;
      const val = normalizeScan(decoded);
      if (!continuous) {
        // H1: a QR in frame fires on MANY frames — accept ONCE, then fully AWAIT the
        // camera teardown before handing off, so the device is released cleanly.
        if (decodedOnceRef.current) return;
        decodedOnceRef.current = true;
        (async () => { await stopCamera(); await handle(val, "camera"); })();
        return;
      }
      // CONTINUOUS (hands-free): accept a code ONCE, then LOCK it until the tag
      // physically LEAVES the frame (detected via empty frames below). No machine-gun.
      if (pausedRef.current) return;
      armedMissRef.current = 0;
      if (val === armedCodeRef.current) return;
      armedCodeRef.current = val;
      const qr = qrRef.current;
      try { qr && qr.pause && qr.pause(); } catch { /* ignore */ }
      (async () => {
        try { await handle(val, "camera"); } catch { /* screen handled it */ }
        await new Promise((r) => setTimeout(r, 350));
        if (active && mountedRef.current && qrRef.current) {
          try { qrRef.current.resume(); } catch { /* ignore */ }
        }
      })();
    };
    const errorCb = () => {
      if (!continuous || !armedCodeRef.current) return;
      armedMissRef.current += 1;
      if (armedMissRef.current >= 4) { armedCodeRef.current = null; armedMissRef.current = 0; }
    };

    const startOnce = async () => {
      const qr = new Html5Qrcode(cameraId, {
        verbose: false,
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.CODE_128],
      });
      qrRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        {
          fps: 15,
          qrbox: (vw, vh) => { const e = Math.floor(Math.min(vw, vh) * 0.72); return { width: e, height: e }; },
          aspectRatio: 1.0,
          videoConstraints: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        },
        decodeCb, errorCb);
      return qr;
    };

    const run = async () => {
      try {
        // H2: one camera at a time — fully release any OTHER live scanner and await it.
        await claimCamera(releaseRef);
        // H3: give the OS a beat to actually free the device before re-acquiring.
        await new Promise((r) => setTimeout(r, CAM_SETTLE_MS));
        if (!active) return;

        let ok = false;
        for (let attempt = 0; attempt < 2 && active && !ok; attempt++) {
          try {
            const qr = await startOnce();
            if (!active) { await releaseQr(qr); qrRef.current = null; return; }
            // H3: confirm the stream is actually delivering frames (not a blank window).
            const el = document.getElementById(cameraId);
            const video = el && el.querySelector("video");
            ok = await waitForVideoFrames(video, CAM_FRAME_TIMEOUT_MS);
            if (!ok) {
              await releaseQr(qr); qrRef.current = null;
              await new Promise((r) => setTimeout(r, 300));
            }
          } catch (e) {
            if (attempt >= 1) throw e;
            try { await releaseQr(qrRef.current); } catch { /* ignore */ }
            qrRef.current = null;
            await new Promise((r) => setTimeout(r, 300));
          }
        }
        if (!active) return;
        if (!ok) {
          // Race lost / camera busy — never leave the operator staring at a dead window.
          setCamErr("Camera didn't return a picture (it may still be busy). Tap 'Scan with camera' to retry, or use the scan gun / type below.");
          await stopCameraQuiet(); setCamOn(false); return;
        }
        try {
          const qr = qrRef.current;
          const caps = (qr.getRunningTrackCapabilities && qr.getRunningTrackCapabilities()) || {};
          setTorchSupported(!!caps.torch);
          setFocusSupported(Array.isArray(caps.focusMode) ? caps.focusMode.length > 0 : !!caps.focusMode);
        } catch { /* capabilities not exposed */ }
      } catch (e) {
        const detail = e && e.name ? `${e.name}${e.message ? ": " + e.message : ""}` : ((e && e.message) || String(e));
        setCamErr(`Camera error — ${detail}. Allow camera access, close other apps using the camera, or use the scan gun / type below.`);
        await stopCameraQuiet();
        setCamOn(false);
      }
    };
    run();
    return () => { active = false; stopCameraQuiet(); };
    // eslint-disable-next-line
  }, [camOn]);

  // Resolves true once the <video> is actually painting frames, false on timeout —
  // the signal that distinguishes a live feed from a blank (frame-less) window.
  const waitForVideoFrames = (video, timeoutMs) => new Promise((resolve) => {
    if (!video) return resolve(false);
    const t0 = Date.now();
    const check = () => {
      if (!mountedRef.current) return resolve(false);
      if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) return resolve(true);
      if (Date.now() - t0 > timeoutMs) return resolve(false);
      requestAnimationFrame(check);
    };
    check();
  });

  const releaseQr = async (qr) => {
    if (qr) {
      try {
        const st = qr.getState && qr.getState();
        if (st === Html5QrcodeScannerState.SCANNING || st === Html5QrcodeScannerState.PAUSED) await qr.stop();
      } catch (e) { console.warn("html5-qrcode stop:", (e && e.message) || e); }
      try { await qr.clear(); } catch { /* ignore */ }
    }
    try {
      const el = document.getElementById(cameraId);
      const video = el && el.querySelector("video");
      const stream = video && video.srcObject;
      if (stream && stream.getTracks) stream.getTracks().forEach((t) => t.stop());
      if (video) video.srcObject = null;
    } catch { /* ignore */ }
  };

  const stopCameraQuiet = async () => {
    const qr = qrRef.current; qrRef.current = null;
    armedCodeRef.current = null; armedMissRef.current = 0;
    await releaseQr(qr);
    unclaimCamera(releaseRef);
    setTorchOn(false); setTorchSupported(false); setFocusSupported(false);
  };
  const stopCamera = async () => { await stopCameraQuiet(); setCamOn(false); };
  // H2: expose THIS instance's full release (stop + collapse view) to the single-camera
  // lock so another scanner can tear us down and await it before acquiring the device.
  releaseRef.current = stopCamera;

  const toggleTorch = async () => {
    const qr = qrRef.current; if (!qr) return;
    const next = !torchOn;
    try { await qr.applyVideoConstraints({ advanced: [{ torch: next }] }); setTorchOn(next); }
    catch { toast.error("Torch isn't available on this device/browser."); }
  };
  const tapFocus = async () => {
    const qr = qrRef.current; if (!qr || !focusSupported) return;
    try { await qr.applyVideoConstraints({ advanced: [{ focusMode: "continuous" }] }); toast.success("Refocusing…"); }
    catch { /* ignore */ }
  };

  // Everything the operator types or a gun types goes through the ONE shared extractor.
  const typed = (raw) => {
    const v = normalizeScan(raw);
    if (v !== (raw || "").trim()) tokenModeRef.current = true;   // a QR URL landed in the box
    return uppercase && !tokenModeRef.current ? v.toUpperCase() : v;
  };

  const handle = async (val, source = "unknown") => {
    const code = normalizeScan(val);
    tokenModeRef.current = false;
    if (!code) { toast.error("Scan or type a code first."); return; }
    // Duplicate-scan debounce: ignore the SAME code within 750ms (double-trigger guard,
    // the #1 rapid-fire data risk). Different codes always pass straight through.
    const ts = Date.now();
    const prev = lastScanRef.current;
    if (code === prev.code && ts - prev.ts < 750) { setScan(""); refocus(); return; }
    lastScanRef.current = { code, ts };
    setScan("");
    // WHICH INPUT, AND THE EXACT CHARACTERS — so the screen can record it on the move itself.
    const meta = { input: source, code: String(val ?? ""), code_used: code,
                   device: (typeof navigator !== "undefined" && navigator.userAgent) || "" };
    keysRef.current = { first: 0, last: 0, events: 0, maxJump: 0 };
    // The screen's onScan owns success/error beeps + flashes + guards (and does NOT
    // advance on error). We only guarantee focus returns to this field afterwards,
    // success OR error, so a misfire stops, beeps (screen), and waits for a re-scan.
    try {
      await onScanRef.current(code, meta);
    } finally {
      refocus();
    }
  };

  // Read the LIVE input value (not React state) so a fast gun scan can't submit a
  // truncated/empty value before the last keystroke commits to state.
  const submit = (e) => { e.preventDefault(); unlockAudio(); handle(inputRef.current?.value ?? scan, judgeInput()); };
  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      const v = e.currentTarget.value;
      if (v && v.trim()) { e.preventDefault(); handle(v, judgeInput()); }
    }
  };

  return (
    <div className="space-y-2">
      <form onSubmit={submit} className="space-y-1.5">
        {label && <Label className={big ? "text-sm font-black uppercase tracking-[0.15em] text-slate-500" : "text-[11px] font-bold uppercase tracking-wider text-slate-400"}>{label}</Label>}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <ScanLine className={`text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 ${big ? "w-8 h-8" : "w-5 h-5"}`} />
            <Input ref={inputRef} data-testid={`${testid}-input`} value={scan}
              onChange={(e) => { noteTyping(scan.length, (e.target.value || "").length); const v = typed(e.target.value); setScan(v); onValueChange?.(v); }}
              onBlur={(e) => onValueChange?.(typed(e.target.value))} onKeyDown={onKeyDown}
              onFocus={() => { lastScanInput = inputRef.current; }} placeholder={placeholder}
              className={big ? "pl-14 font-mono h-24 text-2xl" : "pl-10 font-mono h-12 text-base"} autoComplete="off" inputMode="text" />
          </div>
          <Button type="submit" data-testid={`${testid}-submit`} disabled={busy}
            className={`rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white shrink-0 ${big ? "h-24 px-8 text-xl font-black" : "h-12 px-5 text-base"}`}>
            {busy ? <Loader2 className={`animate-spin ${big ? "w-8 h-8" : "w-5 h-5"}`} /> : submitLabel}
          </Button>
        </div>
      </form>

      {!camOn ? (
        <Button type="button" variant="outline" data-testid={`${testid}-camera-start`}
          onClick={() => { setCamErr(""); unlockAudio(); setCamOn(true); }}
          className={`w-full rounded-lg border-slate-300 ${big ? "h-16 text-lg font-bold" : "h-11"}`}>
          <Camera className={`mr-2 ${big ? "w-7 h-7" : "w-5 h-5"}`} />Scan with camera
        </Button>
      ) : (
        <div className="space-y-2">
          {continuous && (
            <div data-testid={`${testid}-continuous-hint`} className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
              <Camera className="w-3.5 h-3.5" />{paused ? "Camera waiting — answer the question first, then keep scanning." : "Camera stays on — scan the next tag right after each confirmation. No re-tapping."}
            </div>
          )}
          <div className="relative">
            <div id={cameraId} data-testid={`${testid}-camera-view`}
              onClick={focusSupported ? tapFocus : undefined}
              className={`w-full overflow-hidden rounded-lg border border-slate-300 bg-black ${focusSupported ? "cursor-pointer" : ""}`} />
            {focusSupported && (
              <span className="absolute bottom-1.5 left-1.5 text-[10px] font-mono bg-black/60 text-white px-1.5 py-0.5 rounded pointer-events-none">Tap video to refocus</span>
            )}
          </div>
          <div className="flex gap-2">
            {torchSupported && (
              <Button type="button" variant="outline" data-testid={`${testid}-camera-torch`} onClick={toggleTorch}
                className={`flex-1 rounded-lg h-11 ${torchOn ? "border-amber-400 text-amber-700 bg-amber-50" : "border-slate-300"}`}>
                {torchOn ? <FlashlightOff className="w-5 h-5 mr-1.5" /> : <Flashlight className="w-5 h-5 mr-1.5" />}{torchOn ? "Torch off" : "Torch on"}
              </Button>
            )}
            {focusSupported && (
              <Button type="button" variant="outline" data-testid={`${testid}-camera-focus`} onClick={tapFocus}
                className="flex-1 rounded-lg h-11 border-slate-300"><Focus className="w-5 h-5 mr-1.5" />Refocus</Button>
            )}
            <Button type="button" variant="outline" data-testid={`${testid}-camera-stop`} onClick={stopCamera}
              className="flex-1 rounded-lg border-slate-300 h-11">
              <CameraOff className="w-5 h-5 mr-1.5" />Stop
            </Button>
          </div>
        </div>
      )}
      {camErr && <p data-testid={`${testid}-camera-error`} className="text-xs text-[#B01E28]">{camErr}</p>}
    </div>
  );
}
